import { useContext } from "react";
import type { JSX } from "react";
import { useLocalization } from "@fluent/react";
import { ContentCard, TruncatedAccount } from "@narmi/design_system";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";
import AccountContext from "../../contexts/AccountContext";
import InstitutionSettingsContext from "../../contexts/InstitutionSettingsContext";
import DrawerRows from "./DrawerRow";
import type { DrawerRowProps } from "./DrawerRow";

/**
 * the account + transaction-title + company + entry-description rows shared by
 * ACH related payments (single ACH payment, ACH reversal, bulk payment, and bulk reversal)
 */
const getACHRows = (
  l10n: ReturnType<typeof useLocalization>["l10n"],
  {
    fromAccountEl,
    secCode,
    companyName,
    companyId,
    entryDescription,
  }: {
    fromAccountEl?: JSX.Element;
    secCode?: string;
    companyName?: string;
    companyId?: string;
    entryDescription?: string;
  }
): DrawerRowProps[] => {
  const rows: DrawerRowProps[] = [
    [l10n.getString("label-account"), fromAccountEl],
    [
      l10n.getString("ach-payment-transaction-title"),
      secCode ? `${secCode} payment` : undefined,
    ],
  ];

  if (companyName) {
    rows.push([l10n.getString("label-company-name"), companyName]);
  }
  if (companyId) {
    rows.push([l10n.getString("label-company-id"), companyId]);
  }

  rows.push([l10n.getString("label-entry-description"), entryDescription]);

  return rows;
};

type PaymentDetailsCardProps = {
  dualApproval: PaginatedDualApproval;
  originalPayment?: API.ACHPayment | null;
  parentBulkPayment?: API.BulkPayment | null;
};

const PaymentDetailsCard = ({
  dualApproval,
  originalPayment,
  parentBulkPayment,
}: PaymentDetailsCardProps) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext);
  const { wire_display_fee: wireFee } = useContext(InstitutionSettingsContext);

  const { isOwnRequest } = dualApproval;
  const isWire = Boolean(dualApproval.wireId);
  const isInternationalWire = Boolean(dualApproval.internationalWireId);
  const isBulk = Boolean(dualApproval.bulkPaymentId);
  const isBulkReversal = Boolean(dualApproval.bulkReversalId);
  const isFedNow = Boolean(dualApproval.fedNowTransactionId);
  const isWireImport = Boolean(dualApproval.wireImportBatchId);

  // for bulk reversals, the payment-level details (account, SEC code, company,
  // entry description, note) come from the parent bulk payment that is being
  // reversed, which is fetched separately in the drawer
  const resolvedFromAccountId =
    (isBulkReversal ? parentBulkPayment?.institution_account : undefined) ??
    dualApproval.fromAccountId;

  const fromAccount = resolvedFromAccountId
    ? accounts?.find(
        (a: { id: API.AccountId }) => a.id === resolvedFromAccountId
      )
    : undefined;
  const fromAccountEl = fromAccount ? (
    <TruncatedAccount
      name={fromAccount.name}
      lastFour={fromAccount.getMaskedNumber()}
    />
  ) : undefined;

  const rows: DrawerRowProps[] = [];

  if (isWire) {
    rows.push(
      [
        l10n.getString("ach-payment-review-from"),
        dualApproval.fromAccountDisplay,
      ],
      [
        l10n.getString("label-wire-template--template-name"),
        dualApproval.payment?.templateName,
      ],
      [
        l10n.getString("label-wire-template--wire-reason-required"),
        dualApproval.payment?.wireReason,
      ],
      [l10n.getString("label-wire-fee"), wireFee],
      [l10n.getString("label-memo"), dualApproval.payment?.memo]
    );
  } else if (isInternationalWire) {
    rows.push(
      [l10n.getString("label-international-wire-review-from"), fromAccountEl],
      [
        l10n.getString("label-wire-template--wire-reason-required"),
        dualApproval.payment?.wireReason,
      ],
      [l10n.getString("label-memo"), dualApproval.payment?.memo],
      [l10n.getString("label-note-internal"), dualApproval.payment?.note]
    );
  } else if (isFedNow) {
    rows.push(
      [l10n.getString("label-account"), fromAccountEl],
      [
        l10n.getString("label-payment-id"),
        dualApproval.payment?.paymentIdentification ??
          dualApproval.fedNowTransactionId,
      ],
      [l10n.getString("label-memo"), dualApproval.payment?.memo]
    );
  } else if (isWireImport) {
    rows.push(
      [l10n.getString("label-originating-account"), fromAccountEl],
      [
        l10n.getString("label-internal-note"),
        dualApproval.wireImport?.note ?? undefined,
      ]
    );
  } else if (isBulkReversal) {
    rows.push(
      ...getACHRows(l10n, {
        fromAccountEl,
        secCode: parentBulkPayment?.sec_code,
        companyName: parentBulkPayment?.company_name,
        companyId: parentBulkPayment?.company_id,
        entryDescription: l10n.getString("reversal-entry-description"),
      }),
      [l10n.getString("label-note-internal"), parentBulkPayment?.description]
    );
  } else {
    // single ACH payment, single ACH reversal, and bulk payment
    rows.push(
      ...getACHRows(l10n, {
        fromAccountEl,
        secCode:
          originalPayment?.std_ent_cls_code ?? dualApproval.payment?.secCode,
        companyName:
          originalPayment?.company_name ?? dualApproval.payment?.companyName,
        companyId:
          originalPayment?.company_id ?? dualApproval.payment?.companyId,
        entryDescription: dualApproval.achReversalId
          ? l10n.getString("reversal-entry-description")
          : originalPayment?.entry_desc ??
            dualApproval.payment?.entryDescription,
      })
    );

    if (!isBulk) {
      const memo = originalPayment?.description ?? dualApproval.payment?.memo;
      const note = originalPayment?.memo ?? dualApproval.payment?.note;
      rows.push(
        [l10n.getString("label-memo"), memo],
        [l10n.getString("label-note-internal"), note]
      );
    } else {
      rows.push([
        l10n.getString("label-note-internal"),
        dualApproval.payment?.description,
      ]);
    }
  }

  rows.push(
    [
      l10n.getString("label-submitted-by"),
      isOwnRequest ? l10n.getString("label-you") : dualApproval.requesterName,
    ],
    [
      l10n.getString("label-submitted-on"),
      new Date(dualApproval.createdAt).toLocaleDateString("en-US"),
    ]
  );

  return (
    <div className="margin--top--xl">
      <h5 className="margin--bottom--xs fontColor--secondary fontWeight--bold">
        {l10n.getString("ach-payment-payment-details-title").toUpperCase()}
      </h5>
      <ContentCard kind="bordered">
        <DrawerRows rows={rows} />
      </ContentCard>
    </div>
  );
};

export default PaymentDetailsCard;
